import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, BadgeCheck, Check, Eye, EyeOff, Headphones, Loader2, Megaphone, Radio, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useCodeRain } from "@/lib/siteEffects";
import CodeRain from "@/components/marketing/CodeRain";
import { Ambience, Eyebrow, Glass, HairRule, Mono, Reveal } from "@/components/network/NetworkAtoms";
import { PartnerSignupForm } from "@/components/network/PartnerSignupForm";

// Title, description and share card live in src/seo/pages.js, applied by RouteSeo.

const LANES = {
  call_centre: {
    n: "01", Icon: Headphones, eyebrow: "Call centres", short: "Call centre",
    title: "Your agents. Our pipeline.",
    body: "Your agents gather the client's details and the documents a lender needs, and put the case in through the portal. We fit-score it, route it to the desks most likely to fund it and track it all the way to completion. You earn on every deal that funds.",
    points: ["Every agent gets their own seat — they see only the cases they put in; you see the whole floor",
             "Approved scripts and wording, written with the rules in mind",
             "A named Connexis desk that chases the lenders — not your agents",
             "Paid on funded deals, monthly, against a signed agreement"],
    scaleLabel: "Agents on the floor",
    scale: ["1 – 5", "6 – 20", "21 – 50", "50+"],
    placeholder: "Who do you call today, what volumes do you run, and what would you want from us?",
  },
  media: {
    n: "02", Icon: Megaphone, eyebrow: "Media & advertising", short: "Media & advertising",
    title: "Run the ads. Earn on the outcome.",
    body: "Tracked landing pages, campaign-level stats and approved creatives. Your traffic lands on our funding pages; the client data stays with us; you earn when the deal funds — and you can see exactly how it is moving.",
    points: ["Campaign links and QR codes that carry your attribution",
             "A media kit of approved banners and wording — nothing to get wrong",
             "Stats, not personal data: opens, applications, stages, earnings",
             "Paid on funded deals, monthly, against a signed agreement"],
    scaleLabel: "Monthly reach",
    scale: ["Under 10k", "10k – 50k", "50k – 250k", "250k+"],
    placeholder: "Which audiences do you reach, what have you run for finance before, and roughly what budget?",
  },
};

const CHANNELS = ["TikTok ads", "Meta ads", "LinkedIn ads", "YouTube ads", "Google ads", "Email & newsletters",
                  "Content & SEO", "Affiliate network", "Podcast & radio", "Something else"];

const STRIP = ["Tracked attribution", "Approved wording", "Paid on funded deals", "UK GDPR · audit-logged"];

const STEPS = [
  { n: "01", title: "Create your account", body: "Two minutes, below. Sign in straight away; a person on the desk reviews every new partner before the first case." },
  { n: "02", title: "We set you up", body: "Your links, your approved wording, your agreement — e-signed, and re-issued automatically if the terms ever change." },
  { n: "03", title: "Go live. Track. Get paid.", body: "Every application is visible the moment it lands. Commission accrues the day a deal funds, on your next statement." },
];

/* What each lane sees and what stays with us. A call centre handles the client — that is the job — so its
   line is about the panel, not the applicant. A media partner gets full tracking and never has to carry
   the personal data: that is the benefit, so it is worded as one. */
const TRUTH = {
  media: {
    eyebrow: "Full tracking · zero data liability",
    title: <>You see every number.<br />We carry the client data.</>,
    lede: "You get the whole picture on your campaigns — opens, applications, every case's stage and what it is worth to you. What you never have to hold is the personal data. The applicant's identity, contact details and documents stay on Connexis's audited rails, so there is nothing for your company to store, secure or be liable for under UK GDPR.",
    see: ["Opens, starts and applications, per link and per campaign", "Every case's stage, live, as the desk moves it",
          "Facility size bands, so you know what your traffic is producing",
          "What each case is worth to you if it funds", "Owed, paid, and monthly statements"],
    never: ["The applicant's identity — no personal data ever sits on your books",
            "Their email addresses and phone numbers — we make every contact",
            "Documents, bank statements and accounts — stored and secured by us",
            "Which lending desk holds a case — the panel is our side of the line",
            "What any other partner earns — your terms are yours alone"],
    neverLabel: "Stays with Connexis — not your liability",
    neverIcon: ShieldCheck,
  },
  call_centre: {
    eyebrow: "Built on a clear line",
    title: <>Your agents work the client.<br />The lenders stay with us.</>,
    lede: "Your agents gather the details and the documents a case needs — that is the job, and the portal is built for it. What never crosses the line is the panel: which desks we use, what they pay us, and what anyone else earns. Connexis chases the lenders. You see how every case moves.",
    see: ["Every case your floor submits, and which agent put it in",
          "Each agent sees only their own cases; the manager sees the whole floor",
          "Every case's stage, live, and what the desk is waiting on",
          "Document requests and the client's upload link, per case",
          "What each case is worth to you if it funds · owed, paid, monthly statements"],
    never: ["Which lending desk holds a case, or how many",
            "A lender's contact details — the desk works the lenders, not your agents",
            "The terms a desk has with Connexis",
            "Anything another partner earns",
            "Another partner's cases"],
    neverLabel: "Stays with Connexis",
  },
};

const dark = "bg-white/[0.04] border-white/12 text-white placeholder:text-slate-500 focus:border-[#0055FF] focus-visible:ring-0";

export default function PartnerWithUs() {
  const [lane, setLane] = useState("call_centre");
  const [mode, setMode] = useState("signup"); // "signup" | "enquire"
  const [params] = useSearchParams();
  const refId = params.get("ref");
  const [refName, setRefName] = useState("");
  const rain = useCodeRain("partner_with_us", "dark");
  const reduce = useReducedMotion();

  useEffect(() => {
    if (params.get("talk") === "1") setMode("enquire");
  }, [params]);

  useEffect(() => {
    if (!refId) return;
    api.get(`/apply/partner-ref/${refId}`)
      .then(({ data }) => setRefName(data?.partner?.kind === "introducer" ? data.partner.name : ""))
      .catch(() => setRefName(""));
  }, [refId]);

  const T = TRUTH[lane];

  return (
    <main className="relative bg-[#0B1220] text-slate-300 overflow-hidden" data-testid="partner-with-us-page">
      {/* ───────── hero ───────── */}
      <section className="relative overflow-hidden border-b border-white/[0.06]" data-testid="pwu-hero">
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0055FF]/[0.16] to-transparent" />
        <Ambience />
        {/* Held at whisper on purpose — the diagram is the thing to look at here, not the field behind it. */}
        {rain.on && <CodeRain variant="dark" intensity="whisper" className="opacity-60" />}

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-14 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
            <div className="lg:col-span-6">
              <motion.div initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] pl-2.5 pr-3.5 min-h-8 py-1.5 max-w-full" data-testid="pwu-hero-pill">
                  <Radio className="size-3 shrink-0 text-[#7da9ff]" />
                  <Mono className="sm:hidden">Call centres &amp; media</Mono>
                  <Mono className="hidden sm:inline">Campaign partners · call centres &amp; media</Mono>
                </div>
                {refName && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] pl-2.5 pr-3.5 h-8 text-[12px] font-semibold text-emerald-200" data-testid="pwu-invited-by">
                    <BadgeCheck className="size-3.5" /> Introduced by {refName}
                  </div>
                )}
              </motion.div>

              <motion.h1 initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                         transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                         className="mt-7 font-cabinet text-[34px] sm:text-[56px] lg:text-[64px] leading-[1.0] sm:leading-[0.96] tracking-[-0.035em] font-semibold text-white">
                Your reach.<br />
                <span className="text-[#7da9ff]">Our lending network.</span>
              </motion.h1>

              <motion.p initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.16 }}
                        className="mt-6 text-[15.5px] sm:text-[17px] leading-relaxed text-slate-400 max-w-xl">
                We work with outbound call centres and media companies who can put UK businesses that need funding in
                front of the desks that fund them. You bring the audience. We bring the routing, the compliance rails,
                the tracking — and the payouts.
              </motion.p>

              <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.7, delay: 0.24 }}
                          className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                <a href="#enquire" onClick={() => setMode("signup")} data-testid="pwu-cta-signup"
                   className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[14.5px] font-semibold transition-colors shadow-[0_10px_40px_-12px_rgba(0,85,255,0.8)]">
                  Create your account
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </a>
                <a href="#enquire" onClick={() => setMode("enquire")} data-testid="pwu-cta-enquire"
                   className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full border border-white/15 hover:border-white/35 text-white text-[14.5px] font-medium transition-colors">
                  Talk to us first
                </a>
                <Link to="/lending-network" data-testid="pwu-cta-network"
                      className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full text-slate-300 hover:text-white text-[14.5px] font-medium transition-colors">
                  How the network works
                </Link>
              </motion.div>

              <div className="mt-9 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12px] text-slate-500">
                <ShieldCheck className="size-3.5 text-emerald-400/80" />
                Already a partner?
                <Link to="/partner/login" className="text-[#7da9ff] hover:text-white transition-colors underline underline-offset-2" data-testid="pwu-signin">Sign in to your partner portal</Link>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <FlowDiagram reduce={reduce} />
            </div>
          </div>

          <div className="mt-10 sm:mt-14 border-t border-white/[0.08] pt-5 flex flex-wrap items-center gap-x-8 gap-y-3">
            {STRIP.map((s) => (
              <div key={s} className="flex items-center gap-2">
                <span className="size-1 rounded-full bg-[#0055FF]" />
                <Mono className="text-slate-400">{s}</Mono>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── lanes ───────── */}
      <section className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24" data-testid="pwu-lanes">
        <Reveal className="max-w-3xl">
          <Eyebrow>Two ways in</Eyebrow>
          <h2 className="mt-4 font-cabinet text-[32px] sm:text-[44px] leading-[1.04] tracking-[-0.03em] font-semibold text-white">
            Pick the lane that looks like you.
          </h2>
          <p className="mt-4 text-[15px] sm:text-base text-slate-400 leading-relaxed max-w-2xl">
            Different reach, same engine underneath: one application model, one routing fabric, one ledger. The rest of
            this page re-words itself for whichever you choose.
          </p>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          {Object.entries(LANES).map(([k, v]) => {
            const on = lane === k;
            return (
              <Reveal key={k} className="h-full">
                <button type="button" onClick={() => setLane(k)} data-testid={`pwu-lane-${k}`} aria-pressed={on}
                        className={`group relative flex w-full h-full flex-col items-stretch text-left rounded-2xl border p-7 sm:p-8 transition-[transform,border-color,background-color,box-shadow] duration-500 ${
                          on ? "border-[#0055FF]/70 bg-[#0055FF]/[0.07] shadow-[0_30px_80px_-40px_rgba(0,85,255,0.9)]"
                             : "border-white/10 bg-white/[0.03] hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05]"}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-[11px] tracking-[0.3em] ${on ? "text-[#7da9ff]" : "text-slate-500"}`}>{v.n}</span>
                      <span className="h-px w-6 bg-white/15" />
                      <Mono className={on ? "!text-[#7da9ff]" : "!text-slate-400"}>{v.eyebrow}</Mono>
                    </div>
                    <span className={`inline-flex size-10 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                      on ? "border-[#0055FF]/60 bg-[#0055FF] text-white" : "border-white/12 bg-white/[0.04] text-[#7da9ff]"}`}>
                      <v.Icon className="size-4.5" strokeWidth={1.8} />
                    </span>
                  </div>
                  <div className="mt-6 font-cabinet text-[26px] sm:text-[30px] leading-[1.05] tracking-[-0.025em] font-semibold text-white">{v.title}</div>
                  <p className="mt-3.5 text-[14.5px] leading-relaxed text-slate-400">{v.body}</p>
                  <ul className="mt-6 space-y-2.5">
                    {v.points.map((p, j) => (
                      <li key={j} className="flex gap-3 text-[13.5px] text-slate-300 leading-snug" data-testid={on ? `pwu-point-${j}` : undefined}>
                        <Check className={`size-4 mt-0.5 shrink-0 ${on ? "text-[#7da9ff]" : "text-slate-500"}`} /> {p}
                      </li>
                    ))}
                  </ul>
                  <div className={`mt-auto pt-7 inline-flex items-center gap-2 text-[12.5px] font-semibold transition-colors ${on ? "text-white" : "text-slate-400 group-hover:text-slate-200"}`}>
                    {on ? "Selected — the page is set up for you" : "Choose this lane"}
                    <ArrowRight className="size-3.5" />
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      <HairRule />

      {/* ───────── how it works ───────── */}
      <section className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24" data-testid="pwu-how">
        <Reveal className="max-w-3xl">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-4 font-cabinet text-[32px] sm:text-[44px] leading-[1.04] tracking-[-0.03em] font-semibold text-white">
            Three steps. No sales sequence.
          </h2>
        </Reveal>
        <Reveal className="mt-10 grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <Glass key={s.n} className="p-7 h-full flex flex-col" testId={`pwu-step-${i}`}>
              <div className="flex items-center gap-3 h-5">
                <span className="font-mono text-[11px] tracking-[0.3em] text-[#7da9ff]">{s.n}</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <div className="mt-6 text-[18px] font-semibold text-white tracking-[-0.01em] leading-snug">{s.title}</div>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-400">{s.body}</p>
            </Glass>
          ))}
        </Reveal>
      </section>

      {/* ───────── what you see / what stays with us — re-worded per lane ───────── */}
      <section className="relative border-y border-white/[0.06] bg-[#0A1020]" data-testid="pwu-truth" data-lane={lane}>
        <Ambience beams={false} />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal className="max-w-3xl">
              <Eyebrow>{T.eyebrow}</Eyebrow>
              <h2 className="mt-4 font-cabinet text-[32px] sm:text-[44px] leading-[1.04] tracking-[-0.03em] font-semibold text-white" data-testid="pwu-truth-title">
                {T.title}
              </h2>
              <p className="mt-4 text-[15px] sm:text-base text-slate-400 leading-relaxed max-w-2xl">{T.lede}</p>
            </Reveal>
            <LaneSwitch lane={lane} setLane={setLane} testId="pwu-truth-lane" />
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <Reveal className="h-full">
              <Glass className="p-7 sm:p-8 h-full" hover={false} testId="pwu-see">
                <div className="flex items-center gap-2.5"><Eye className="size-4 text-emerald-400" /><Mono className="!text-emerald-300">In your portal</Mono></div>
                <ul className="mt-6 space-y-3">
                  {T.see.map((x) => <li key={x} className="flex gap-3 text-[14.5px] text-slate-200"><Check className="size-4 mt-0.5 shrink-0 text-emerald-400" />{x}</li>)}
                </ul>
              </Glass>
            </Reveal>
            <Reveal className="h-full">
              <Glass className="p-7 sm:p-8 h-full" hover={false} testId="pwu-never">
                <div className="flex items-center gap-2.5">
                  {React.createElement(T.neverIcon || EyeOff, { className: "size-4 shrink-0 text-slate-400" })}
                  <Mono className="!text-slate-400">{T.neverLabel}</Mono>
                </div>
                <ul className="mt-6 space-y-3">
                  {T.never.map((x) => <li key={x} className="flex gap-3 text-[14.5px] text-slate-400"><span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-slate-500" />{x}</li>)}
                </ul>
              </Glass>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="mt-5">
            <Glass className="p-6 sm:p-7 flex gap-4 items-start" hover={false} testId="pwu-compliance">
              <ShieldCheck className="size-5 shrink-0 text-[#7da9ff] mt-0.5" strokeWidth={1.7} />
              <p className="text-[14px] leading-relaxed text-slate-400">
                <span className="text-white font-semibold">We are careful about who we work with.</span> Outbound calls about
                finance and financial promotions carry rules — TPS and CTPS screening, UK GDPR, and FCA financial-promotion
                requirements where regulated activity is involved. Every campaign runs on wording we have approved, every
                partner signs the same agreement our introducers do, and we will say no to a campaign that does not fit.
              </p>
            </Glass>
          </Reveal>
        </div>
      </section>

      {/* ───────── sign up / enquiry ───────── */}
      <section id="enquire" className="relative scroll-mt-20 overflow-hidden" data-testid="pwu-enquire" data-mode={mode}>
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_120%,rgba(0,85,255,0.26),transparent_62%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <Eyebrow>{mode === "signup" ? "Create your account" : "Start a conversation"}</Eyebrow>
            <h2 className="mt-4 font-cabinet text-[34px] sm:text-[48px] leading-[1.02] tracking-[-0.035em] font-semibold text-white" data-testid="pwu-enquire-title">
              {mode === "signup" ? <>Set up today.<br />Approved tomorrow.</> : <>Tell us about<br />your reach.</>}
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-slate-400 max-w-md">
              {mode === "signup"
                ? "Create your account and sign in straight away. A person on the desk reviews every new partner before the first case — usually within one working day — and your agreement arrives to e-sign the moment you are approved."
                : "A person from the desk reads every enquiry and comes back to you, usually the same working day. If it is a fit we set up a call and walk you through the campaign, the wording and the agreement."}
              {refName && <> You will be set up as <span className="text-white font-semibold">{refName}</span>&apos;s recruit — they earn an override from Connexis, never from you.</>}
            </p>

            <div className="mt-7 inline-flex rounded-full border border-white/12 bg-white/[0.03] p-1" data-testid="pwu-mode" role="tablist">
              {[["signup", "Create my account"], ["enquire", "Talk to us first"]].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`pwu-mode-${k}`}
                        className={`h-9 px-4 rounded-full text-[12.5px] font-semibold transition-colors ${
                          mode === k ? "bg-[#0055FF] text-white" : "text-slate-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-1 text-[12.5px] text-slate-500">
              <p>Already signed up? <Link to="/partner/login" className="text-[#7da9ff] hover:text-white underline underline-offset-2 transition-colors" data-testid="pwu-signin-2">Sign in to your partner portal</Link>.</p>
              <p>An introducer? Your recruitment links live in your <Link to="/introducer/login" className="text-[#7da9ff] hover:text-white underline underline-offset-2 transition-colors">introducer portal</Link>.</p>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-7">
            {mode === "signup"
              ? <PartnerSignupForm lane={lane} setLane={setLane} refId={refName ? refId : null} refName={refName} />
              : <EnquiryForm lane={lane} setLane={setLane} refId={refName ? refId : null} />}
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function LaneSwitch({ lane, setLane, testId }) {
  return (
    <div className="inline-flex rounded-full border border-white/12 bg-white/[0.03] p-1" data-testid={testId} role="tablist">
      {Object.entries(LANES).map(([k, v]) => (
        <button key={k} type="button" role="tab" aria-selected={lane === k} onClick={() => setLane(k)} data-testid={`${testId}-${k}`}
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-semibold transition-colors ${
                  lane === k ? "bg-[#0055FF] text-white" : "text-slate-400 hover:text-white"}`}>
          <v.Icon className="size-3.5" strokeWidth={1.9} /> {v.short}
        </button>
      ))}
    </div>
  );
}

/* Three sources of reach feeding one core, fanning out to desks. Pulses ride the paths.
   The stroke gradient is in user space on purpose: a bounding-box gradient has zero height on a
   perfectly horizontal path, so the middle line used to vanish. */
function FlowDiagram({ reduce }) {
  const sources = [["Calls", 70], ["Ads", 150], ["Content", 230]];
  const desks = [["Desk A", 60], ["Desk B", 120], ["Desk C", 180], ["Desk D", 240]];
  const paths = [
    ...sources.map(([, y]) => `M 130 ${y} C 210 ${y}, 220 150, 290 150`),
    ...desks.map(([, y]) => `M 310 150 C 400 150, 410 ${y}, 500 ${y}`),
  ];
  return (
    <div className="relative aspect-[5/3] w-full" data-testid="pwu-flow">
      <svg viewBox="0 0 600 300" className="absolute inset-0 h-full w-full" fill="none">
        <defs>
          <linearGradient id="pwu-line" gradientUnits="userSpaceOnUse" x1="130" y1="0" x2="500" y2="0">
            <stop offset="0" stopColor="#7da9ff" stopOpacity="0.2" />
            <stop offset="0.5" stopColor="#7da9ff" stopOpacity="0.6" />
            <stop offset="1" stopColor="#7da9ff" stopOpacity="0.2" />
          </linearGradient>
          <radialGradient id="pwu-core">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="0.35" stopColor="#7da9ff" />
            <stop offset="1" stopColor="#0055FF" stopOpacity="0" />
          </radialGradient>
        </defs>
        {paths.map((d, i) => <path key={i} id={`pwu-p${i}`} d={d} stroke="url(#pwu-line)" strokeWidth="1.2" data-testid={`pwu-path-${i}`} />)}
        {!reduce && paths.map((_, i) => (
          <circle key={`c${i}`} r="3" fill="#ffffff">
            <animateMotion dur={`${2.6 + (i % 3) * 0.5}s`} begin={`${(i * 0.37) % 2.2}s`} repeatCount="indefinite">
              <mpath href={`#pwu-p${i}`} />
            </animateMotion>
          </circle>
        ))}
        {sources.map(([label, y]) => (
          <g key={label}>
            <circle cx="120" cy={y} r="18" fill="#0B1220" stroke="rgba(255,255,255,0.14)" />
            <circle cx="120" cy={y} r="4" fill="#7da9ff" />
            <text x="92" y={y + 4} textAnchor="end" fill="#94a3b8" fontSize="11" fontFamily="ui-monospace, monospace" letterSpacing="1.5"
                  data-testid={`pwu-source-${label.toLowerCase()}`}>{label.toUpperCase()}</text>
          </g>
        ))}
        <circle cx="300" cy="150" r="46" fill="url(#pwu-core)" opacity="0.9" />
        <circle cx="300" cy="150" r="14" fill="#0B1220" stroke="#ffffff" strokeWidth="1.5" />
        <circle cx="300" cy="150" r="5" fill="#0055FF" />
        <text x="300" y="214" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontFamily="ui-monospace, monospace" letterSpacing="2">CONNEXIS ROUTER</text>
        {desks.map(([label, y]) => (
          <g key={label}>
            <rect x="505" y={y - 12} width="62" height="24" rx="7" fill="#0B1220" stroke="rgba(255,255,255,0.14)" />
            <text x="536" y={y + 4} textAnchor="middle" fill="#cbd5e1" fontSize="10" fontFamily="ui-monospace, monospace" letterSpacing="1.2">{label.toUpperCase()}</text>
          </g>
        ))}
      </svg>
      <div className="pointer-events-none absolute inset-x-0 -bottom-3 hidden sm:flex justify-center" data-testid="pwu-flow-legend">
        <div className="flex items-center gap-2.5 rounded-full border border-white/[0.08] bg-[#0B1220]/85 px-4 h-8 text-[11.5px] text-slate-300 backdrop-blur-sm">
          <span className="text-white font-medium">Your reach</span>
          <span className="h-px w-5 bg-white/25" />
          <span>one application model</span>
          <span className="h-px w-5 bg-white/25" />
          <span>routed to the desks that fund it</span>
          <span className="h-px w-5 bg-white/25" />
          <span className="text-emerald-300 font-medium">funded</span>
        </div>
      </div>
    </div>
  );
}

/* A chip group — the same control for agents on the floor, monthly reach and ad channels, so no
   native dropdown ever paints black over the dark card. */
function Chips({ options, value, onPick, multi = false, testId }) {
  const picked = (o) => (multi ? value.includes(o) : value === o);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onPick(o)} aria-pressed={picked(o)} data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`h-9 rounded-full border px-3.5 text-[12.5px] font-medium transition-colors ${
                  picked(o) ? "bg-[#0055FF]/[0.16] border-[#0055FF] text-white" : "border-white/12 text-slate-300 hover:border-white/30"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

const FieldLabel = ({ children, hint }) => (
  <div className="flex items-baseline justify-between gap-3 mb-2">
    <Mono className="!text-slate-400">{children}</Mono>
    {hint && <span className="text-[11.5px] text-slate-500">{hint}</span>}
  </div>
);

function EnquiryForm({ lane, setLane, refId }) {
  const [kind, setKind] = useState(lane);
  useEffect(() => { setKind(lane); }, [lane]);
  const pick = (k) => { setKind(k); if (k in LANES) setLane(k); };
  const L = LANES[kind] || LANES[lane];
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", website: "", scale: "", other: "", message: "" });
  const [channels, setChannels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleChannel = (c) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/network/enquiry", {
        kind, source: "partner-with-us", referred_by: refId || null,
        name: f.name.trim(), company: f.company.trim() || null, email: f.email.trim(),
        phone: f.phone.trim() || null, website: f.website.trim() || null,
        scale: kind === "unsure" ? (f.other.trim() || null) : (f.scale || null),
        channels: kind === "media" && channels.length ? channels : null,
        message: f.message.trim() || null,
      });
      setSent(true);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't send that — try again");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <Glass className="p-8 sm:p-10" hover={false} testId="pwu-sent">
        <span className="inline-flex size-11 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300"><Check className="size-5" /></span>
        <div className="mt-5 font-cabinet text-[26px] tracking-[-0.02em] font-semibold text-white">Got it — thank you.</div>
        <p className="mt-2 text-[14.5px] text-slate-400 leading-relaxed">
          A person from the desk will come back to you, usually the same working day. Check your inbox for a confirmation.
        </p>
      </Glass>
    );
  }

  return (
    <Glass className="p-6 sm:p-8" hover={false} testId="pwu-form">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <FieldLabel>You are</FieldLabel>
          <div className="grid grid-cols-3 gap-2" data-testid="pwu-kind-row">
            {[["call_centre", "A call centre", "A call centre"], ["media", "Media & advertising", "Media & ads"], ["unsure", "Something else", "Other"]].map(([k, label, short]) => (
              <button key={k} type="button" onClick={() => pick(k)} data-testid={`pwu-kind-${k}`} aria-pressed={k === kind}
                      className={`h-10 px-1 rounded-full border whitespace-nowrap text-[11px] sm:text-[12.5px] font-semibold transition-colors ${
                        k === kind ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-white/12 text-slate-300 hover:border-white/30"}`}>
                <span className="min-[360px]:hidden">{short}</span>
                <span className="hidden min-[360px]:inline sm:hidden">{k === "unsure" ? label : short}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Input required value={f.name} onChange={set("name")} placeholder="Your name" className={`h-11 ${dark}`} data-testid="pwu-name" />
          <Input value={f.company} onChange={set("company")} placeholder="Company" className={`h-11 ${dark}`} data-testid="pwu-company" />
          <Input required type="email" value={f.email} onChange={set("email")} placeholder="Work email" className={`h-11 ${dark}`} data-testid="pwu-email" />
          <Input value={f.phone} onChange={set("phone")} placeholder="Phone (optional)" className={`h-11 ${dark}`} data-testid="pwu-phone" />
          <Input value={f.website} onChange={set("website")} placeholder="Website (optional)" className={`h-11 ${dark} sm:col-span-2`} data-testid="pwu-website" />
        </div>

        {kind === "media" && (
          <div data-testid="pwu-media-fields">
            <FieldLabel hint="pick every channel you run">Channels you run</FieldLabel>
            <Chips options={CHANNELS} value={channels} onPick={toggleChannel} multi testId="pwu-channel" />
            <div className="mt-4">
              <FieldLabel hint="optional">{L.scaleLabel}</FieldLabel>
              <Chips options={L.scale} value={f.scale} onPick={(o) => setF({ ...f, scale: f.scale === o ? "" : o })} testId="pwu-scale" />
            </div>
          </div>
        )}

        {kind === "call_centre" && (
          <div data-testid="pwu-centre-fields">
            <FieldLabel hint="roughly is fine">{L.scaleLabel}</FieldLabel>
            <Chips options={L.scale} value={f.scale} onPick={(o) => setF({ ...f, scale: f.scale === o ? "" : o })} testId="pwu-scale" />
          </div>
        )}

        {kind === "unsure" && (
          <div data-testid="pwu-other-fields">
            <FieldLabel hint="a line is plenty">What do you do?</FieldLabel>
            <Input value={f.other} onChange={set("other")} placeholder="e.g. a broker network, a trade body, software with SME customers…"
                   className={`h-11 ${dark}`} data-testid="pwu-other" />
          </div>
        )}

        <div>
          <FieldLabel hint="optional">Anything else</FieldLabel>
          <Textarea value={f.message} onChange={set("message")} placeholder={kind in LANES ? L.placeholder : "Tell us what you have in mind and who you reach."} className={`min-h-[110px] ${dark}`} data-testid="pwu-message" />
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
          <p className="text-[11.5px] text-slate-500">One confirmation email. We never pass your details on.</p>
          <Button type="submit" disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] h-11 px-6 font-semibold" data-testid="pwu-submit">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <>Send it <ArrowRight className="size-4 ml-1" /></>}
          </Button>
        </div>
      </form>
    </Glass>
  );
}
